import React, { useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../../app/hooks'
import { interpreterSelector } from '../../features/interpreter/interpreterSlice'
import { useInterpreterSubscriptions } from '../../ws/useInterpreterSubscriptions'
import { CallStatus } from '../../misc/domain/room'

/**
 * Global interpreter view — a standalone full-screen "wallboard" for a monitor in the interpreter
 * room. It reuses the existing interpreter WebSocket feed (`/topic/interpreters` →
 * `WsInterpreterUpdate`), so it is real-time with no extra backend. Tiles:
 *   - Interpreters connected  (roomMetrics.connected_interpreters)
 *   - Incoming calls          (rooms in PENDING) — pulses; turns red when an emergency is ringing
 *   - Active calls            (rooms in IN_CALL)
 *   - Users waiting           (roomMetrics.users_waiting)
 *
 * Must be opened by a logged-in interpreter account — the server only streams this feed to
 * interpreters. Full-screen, no app chrome (mounted outside WrapperPage).
 */

const COLORS = {
  bg: '#0B1F20',
  tile: '#12312F',
  tileBorder: 'rgba(60, 201, 206, 0.14)',
  interpreters: '#3CC9CE',
  incoming: '#F7B551',
  active: '#97C255',
  waiting: '#8FB0B2',
  emergency: '#E24B4B',
  label: '#9FC3C4',
  muted: '#5E8082',
}

interface TileProps {
  label: string
  count: number
  color: string
  pulse?: boolean
}

const Tile: React.FC<TileProps> = ({ label, count, color, pulse }) => (
  <div
    style={{
      flex: '1 1 0',
      minWidth: 260,
      background: COLORS.tile,
      border: `2px solid ${pulse ? color : COLORS.tileBorder}`,
      borderRadius: 20,
      padding: '3vh 2vw',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: pulse ? `0 0 40px ${color}55` : 'none',
      animation: pulse ? 'iris-wallboard-pulse 1.4s ease-in-out infinite' : undefined,
      transition: 'border-color .3s, box-shadow .3s',
    }}
  >
    <div
      style={{
        fontSize: '13vw',
        lineHeight: 1,
        fontWeight: 900,
        fontFamily: 'Alegreya Sans, sans-serif',
        color,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {count}
    </div>
    <div
      style={{
        marginTop: '2vh',
        fontSize: '1.9vw',
        letterSpacing: 1,
        textTransform: 'uppercase',
        color: COLORS.label,
        textAlign: 'center',
      }}
    >
      {label}
    </div>
  </div>
)

const WallboardPage: React.FC = () => {
  // Subscribe to the interpreter feed (same as the interpreter dashboard).
  useInterpreterSubscriptions()
  const { rooms, roomMetrics } = useAppSelector(interpreterSelector)

  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  const incomingRooms = rooms.filter(r => r.status === CallStatus.PENDING)
  const activeCount = rooms.filter(r => r.status === CallStatus.IN_CALL).length
  const incomingCount = incomingRooms.length
  const hasEmergency = incomingRooms.some(r => r.is_emergency)

  // Audible nudges — so an interpreter looking away/headset down still notices a call. Browsers block
  // autoplay until a user gesture, so the wallboard needs one "enable sound" click when it's set up.
  const [soundEnabled, setSoundEnabled] = useState(false)
  const chimeRef = useRef<HTMLAudioElement>()
  const alarmRef = useRef<HTMLAudioElement>()
  const prevIncoming = useRef(0)

  useEffect(() => {
    chimeRef.current = new Audio('/sound/new_call_bell.mp3')
    const alarm = new Audio('/sound/new_call_bell_2.mp3')
    alarm.loop = true // emergencies loop until answered/cleared
    alarmRef.current = alarm
    return () => { alarmRef.current?.pause() }
  }, [])

  // Enable-sound unlocks both clips within the click gesture (muted, so no blip).
  const enableSound = () => {
    [chimeRef.current, alarmRef.current].forEach(a => {
      if (!a) return
      const vol = a.volume
      a.volume = 0
      a.play().then(() => { a.pause(); a.currentTime = 0; a.volume = vol }).catch(() => { a.volume = vol })
    })
    setSoundEnabled(true)
  }

  // Single chime whenever the incoming count rises (a new call arrived).
  useEffect(() => {
    if (soundEnabled && incomingCount > prevIncoming.current) chimeRef.current?.play().catch(() => {})
    prevIncoming.current = incomingCount
  }, [incomingCount, soundEnabled])

  // Emergency alarm: loop while an emergency is ringing (PENDING); stop the instant it's answered
  // (status → IN_CALL) or otherwise resolved — hasEmergency goes false and we pause + reset.
  useEffect(() => {
    const alarm = alarmRef.current
    if (!alarm) return
    if (soundEnabled && hasEmergency) alarm.play().catch(() => {})
    else { alarm.pause(); alarm.currentTime = 0 }
  }, [hasEmergency, soundEnabled])

  const clock = now.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: COLORS.bg,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        padding: '3vh 3vw',
        boxSizing: 'border-box',
        fontFamily: 'Alegreya Sans, sans-serif',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes iris-wallboard-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '3vh' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '1vw' }}>
          <span style={{ fontSize: '2.6vw', fontWeight: 900, color: COLORS.interpreters, letterSpacing: 2 }}>IRIS</span>
          <span style={{ fontSize: '1.6vw', color: COLORS.muted, letterSpacing: 1 }}>ΚΕΝΤΡΟ ΔΙΕΡΜΗΝΕΙΑΣ</span>
        </div>
        <div style={{ fontSize: '2.2vw', color: COLORS.label, fontVariantNumeric: 'tabular-nums' }}>{clock}</div>
      </div>

      {/* Emergency banner */}
      {hasEmergency && (
        <div
          style={{
            marginBottom: '2.5vh',
            padding: '1.6vh 2vw',
            borderRadius: 14,
            background: COLORS.emergency,
            color: '#fff',
            fontSize: '2.4vw',
            fontWeight: 900,
            textAlign: 'center',
            letterSpacing: 2,
            animation: 'iris-wallboard-pulse 1.1s ease-in-out infinite',
          }}
        >
          ⚠ ΕΠΕΙΓΟΥΣΑ ΚΛΗΣΗ
        </div>
      )}

      {/* Tiles */}
      <div style={{ flex: 1, display: 'flex', gap: '2vw', alignItems: 'stretch' }}>
        <Tile label="Διερμηνείς σε σύνδεση" count={roomMetrics.connected_interpreters} color={COLORS.interpreters} />
        <Tile
          label="Εισερχόμενες κλήσεις"
          count={incomingCount}
          color={hasEmergency ? COLORS.emergency : COLORS.incoming}
          pulse={incomingCount > 0}
        />
        <Tile label="Ενεργές κλήσεις" count={activeCount} color={COLORS.active} />
        <Tile label="Χρήστες σε αναμονή" count={roomMetrics.users_waiting} color={COLORS.waiting} />
      </div>

      {/* One-time sound unlock (browsers block autoplay until a user gesture). */}
      {!soundEnabled && (
        <div
          onClick={enableSound}
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(11, 31, 32, 0.94)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
          }}
        >
          <div style={{ fontSize: '5vw' }}>🔊</div>
          <div style={{ fontSize: '1.8vw', color: COLORS.label, marginTop: '2vh', letterSpacing: 1 }}>
            Πατήστε για ενεργοποίηση ήχου
          </div>
        </div>
      )}
    </div>
  )
}

export default WallboardPage
